import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import confetti from "canvas-confetti";
import { useAuth } from "../auth/AuthProvider";
import Goal from "../models/Goal";
import GoalAttempt from "../models/GoalAttempt";
import "react-datepicker/dist/react-datepicker.css";

const LogGoalAttemptPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialGoalId = searchParams.get("goal") || "";

  const [goals, setGoals] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(initialGoalId);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [value, setValue] = useState("");
  const [reps, setReps] = useState("");
  const [notes, setNotes] = useState("");
  const [perceivedEffort, setPerceivedEffort] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentBest, setCurrentBest] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (user) {
      loadGoals();
    }
  }, [user]);

  useEffect(() => {
    if (selectedGoal && user) {
      loadCurrentBest();
    }
  }, [selectedGoal, user]);

  const loadGoals = async () => {
    const goalList = await Goal.getActive(user.uid);
    setGoals(goalList);
  };

  const loadCurrentBest = async () => {
    const goal = getCurrentGoal();
    if (!goal) return;

    const pb = await GoalAttempt.getPersonalBest(user.uid, selectedGoal, goal.direction);
    if (pb) {
      setCurrentBest(pb.value);
      setValue(pb.value.toString());
    } else if (goal.startingValue) {
      setValue(goal.startingValue.toString());
    }
  };

  const getCurrentGoal = () => {
    return goals.find(g => g.id === selectedGoal);
  };

  const triggerConfetti = () => {
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ['#6366f1', '#8b5cf6', '#22c55e', '#eab308'];

    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedGoal) {
      alert("Please select a goal!");
      return;
    }

    if (!value) {
      alert("Please enter a value!");
      return;
    }

    setLoading(true);
    const formattedDate = selectedDate.toISOString().split("T")[0];
    const goal = getCurrentGoal();
    const numValue = parseFloat(value);

    try {
      await GoalAttempt.create(user.uid, selectedGoal, goal, {
        date: formattedDate,
        value: numValue,
        reps: reps ? parseInt(reps, 10) : null,
        notes,
        perceivedEffort,
      });

      // Check if goal was achieved
      const isGoalCompleted = GoalAttempt.isGoalCompleted(goal, numValue);
      if (isGoalCompleted && goal.status === "active") {
        await Goal.updateStatus(user.uid, selectedGoal, "completed");
        triggerConfetti();
        setSuccessMessage(`Goal achieved! ${numValue} ${goal.targetUnit}`);
      } else {
        // Check if personal best
        const isPB = goal.direction === "increase"
          ? (currentBest === null || numValue > currentBest)
          : (currentBest === null || numValue < currentBest);

        if (isPB) {
          setSuccessMessage(`New personal best! ${numValue} ${goal.targetUnit}`);
        } else {
          setSuccessMessage(`Logged ${numValue} ${goal.targetUnit} for ${goal.title}`);
        }
      }

      setNotes("");
      setPerceivedEffort(null);
      setCurrentBest(numValue);

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const goal = getCurrentGoal();

  return (
    <div className="max-w-md md:max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 md:p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Log Attempt
        </h1>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Goal Select */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Goal
            </label>
            <select
              value={selectedGoal}
              onChange={(e) => setSelectedGoal(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
              required
            >
              <option value="">Select a goal</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title} ({g.targetValue} {g.targetUnit})
                </option>
              ))}
            </select>
          </div>

          {/* Date Picker */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() - 1);
                  setSelectedDate(newDate);
                }}
                className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 text-center">
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  dateFormat="EEE, d MMM yyyy"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-base focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  maxDate={new Date()}
                  popperPlacement="top"
                  wrapperClassName="w-full"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() + 1);
                  if (newDate <= new Date()) {
                    setSelectedDate(newDate);
                  }
                }}
                disabled={selectedDate.toDateString() === new Date().toDateString()}
                className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Value Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Value {goal && `(${goal.targetUnit})`}
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={goal?.targetValue?.toString() || ""}
                step="0.1"
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
              {goal?.targetReps && (
                <>
                  <span className="text-gray-500 dark:text-gray-400">x</span>
                  <input
                    type="number"
                    value={reps}
                    onChange={(e) => setReps(e.target.value)}
                    placeholder={goal.targetReps?.toString() || ""}
                    className="w-20 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <span className="text-gray-500 dark:text-gray-400 text-sm">reps</span>
                </>
              )}
            </div>
            {goal && (
              <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-2">
                <span>Target: {goal.targetValue} {goal.targetUnit}{goal.targetReps && ` x${goal.targetReps}`}</span>
                {currentBest !== null && <span>Current best: {currentBest}</span>}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How did it go?"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Perceived Effort */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Perceived Effort (RPE) - optional
            </label>
            <div className="flex gap-1 sm:gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setPerceivedEffort(perceivedEffort === num ? null : num)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    perceivedEffort === num
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>Easy</span>
              <span>Max effort</span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading || !selectedGoal}
              className="w-1/2 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded font-medium transition-colors"
            >
              {loading ? "Saving..." : "Log Attempt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogGoalAttemptPage;
